import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/collections")({
  component: CollectionsPage,
});

import { useState } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchCollections,
  useCollectionMutations,
} from "@/hooks/api/inventory/useCollectionsLabels";
import { CollectionTable } from "@/components/inventory/tables/CollectionTable";
import { CollectionFormSheet } from "@/components/inventory/forms/CollectionFormSheet";
import type { Collection, CollectionPayload } from "@/types/inventory";

function CollectionsPage() {
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const collectionsQuery = useFetchCollections({
    page_size: pageSize,
    page,
  });
  const collections = collectionsQuery.data?.results ?? [];
  const count = collectionsQuery.data?.count ?? 0;

  const { createCollection, updateCollection, deleteCollection } =
    useCollectionMutations();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editing, setEditing] = useState<Collection | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Collection | null>(null);

  const isMutating =
    createCollection.isPending ||
    updateCollection.isPending ||
    deleteCollection.isPending;
  const isLoading = collectionsQuery.isPending || isMutating;
  const isFetching = collectionsQuery.isFetching || isMutating;

  const handleSubmit = async (data: CollectionPayload) => {
    if (editing) {
      await updateCollection.mutateAsync({ id: editing.id, payload: data });
      toast.success("Collection updated");
    } else {
      await createCollection.mutateAsync(data);
      toast.success("Collection created");
    }
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCollection.mutateAsync({ id: deleteTarget.id });
      toast.success("Collection deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader
        breadcrumbs={[
          { label: "Inventory" },
          { label: "Collections" },
        ]}
      />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Collections"
          description="Group items into logical containers (kits, vehicles, client gear)"
          onAdd={() => {
            setEditing(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="Add Collection"
          isLoading={isFetching}
        />

        <CollectionTable
          collections={collections}
          isLoading={isLoading}
          onView={(c) => {
            setEditing(c);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(c) => {
            setEditing(c);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(c) => setDeleteTarget(c)}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <CollectionFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleSubmit}
          editing={editing}
          isLoading={isMutating}
          isViewOnly={isViewOnly}
        />

        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Collection"
          description={`Delete collection "${deleteTarget?.name}"? Items in this collection will be unassigned.`}
          itemName={deleteTarget?.name}
          isLoading={deleteCollection.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
